import * as React from 'react'
import { useRef, useState, useContext, useCallback, useEffect } from 'react'
import gsap from 'gsap'
import { SplitText } from 'gsap/SplitText'
import { useGSAP } from '@gsap/react'
import styles from './SpeechFullViewPractice.module.scss'
import voices from '../speech/voices.js'
import { speak } from '../speech/speak.js'
import DeviceContext from '../context/DeviceContext.js'
import languages from '../languages.js'
import { getDeeplLanguageAcronym } from '../query/deeplLanguages.js'
import { useApolloClient } from '@apollo/client'
import UserContext from '../context/UserContext.js'
import { getLanguageAcronym } from '../languages.js'
import flags from '../flags.js'
import TextField from '@mui/material/TextField'
import SpeedDial from '@mui/material/SpeedDial'
import SpeedDialAction from '@mui/material/SpeedDialAction'
import { Typography, Box } from '@mui/material'
import Fab from '@mui/material/Fab'
import RefreshIcon from '@mui/icons-material/Refresh'
import CircularProgress from '@mui/material/CircularProgress'
import Diversity3Icon from '@mui/icons-material/Diversity3'
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied'
import StorageIcon from '@mui/icons-material/Storage'
import SmsIcon from '@mui/icons-material/Sms'
import WorkOutlineIcon from '@mui/icons-material/WorkOutline'
import {
  USER_SENTENCES_WITH_TRANSLATIONS_QUERY,
  SHARED_SENTENCES_WITH_TRANSLATIONS_QUERY,
  TATOEBA_SENTENCES_QUERY,
  GET_ALL_TRANSLATION_USER_SENTENCES
} from '../query/gqlQueries.js'
import {
  mapUserSentencesDataSpecificLang,
  mapSharedSentencesDataSpecificLang,
  mapTatoebaSentencesData,
  mapUserSentencesDataOneToMany,
  mapSharedSentencesDataOneToMany,
  mapTatoebaSentencesDataOneToMany
} from '../query/queryMappers.js'
import SpeechPracticeRecorder from '../speech/SpeechPracticeRecorder.js'
import { gql } from '@apollo/client'
gsap.registerPlugin(SplitText)

const GET_ALL_TRANSLATION_SHARED_SENTENCES = gql`
  query AllSharedSentencesWithTranslations($username: String!, $sourceLang: String) {
    allSharedSentencesWithTranslations(username: $username, sourceLang: $sourceLang) {
      sentence {
        id
        text
        lang
        user
        dateAdded
        dateLastModified
      }
      translations {
        id
        text
        lang
        user
        dateAdded
        dateLastModified
      }
    }
  }
`

export default function SpeechFullViewPractice({ voiceSpeed, isAllViewSpeechPracticeVisible, setIsAllViewSpeechPracticeVisible }) {
  const [feedback, setFeedback] = useState('')
  const { username } = useContext(UserContext)
  const [currentTranslationIndex, setCurrentTranslationIndex] = useState(0)
  const [spokenText, setSpokenText] = useState('')
  // --- START FIX: Use 3-letter acronyms for initial spokenLang ---
  const initialTargetLanguage = 'Portuguese'
  // Get the 3-letter acronym ('por') for initialization
  const initialSpokenLang = getLanguageAcronym(initialTargetLanguage)
  const [spokenLang, setSpokenLang] = useState(initialSpokenLang)
  const [expectedText, setExpectedText] = useState('')
  const [targetLanguage, setTargetLanguage] = useState(initialTargetLanguage)
  // --- END FIX ---
  const { isMobile } = useContext(DeviceContext)
  const targetRef = useRef(null)
  const sourceRef = useRef(null)
  const swipeStartX = useRef(0)
  const swipeEndX = useRef(0)
  const swipeStartY = useRef(0)
  const swipeEndY = useRef(0)
  const apolloClient = useApolloClient()
  const [sentenceListInternal, setSentenceListInternal] = useState([])
  const [loadingFetch, setLoadingFetch] = useState(false)
  const [sourceLanguage] = useState('English')
  const [searchWord, setSearchWord] = useState('')
  const [owner, setOwner] = useState('My')
  const [label, setLabel] = useState('Sentences')
  const [isOwnerSpeedDialOpen, setIsOwnerSpeedDialOpen] = useState(false)
  const [isLabelSpeedDialOpen, setIsLabelSpeedDialOpen] = useState(false)
  const [isLanguageSpeedDialOpen, setIsLanguageSpeedDialOpen] = useState(false)
  const [highlightedExpected, setHighlightedExpected] = useState([])
  const [correctWords, setCorrectWords] = useState(new Set())
  const isAnySpeedDialOpen = () => isOwnerSpeedDialOpen || isLabelSpeedDialOpen || isLanguageSpeedDialOpen
  // ... (Keep your owners, labels, iconMapping, queries, handleSelectLabel, handleLanguageChange, handleSearch definitions here) ...
  const owners = ['My', 'Shared', 'Tatoeba']
  const labels = ['Sentences', 'Many']
  const languageOptions = Object.keys(languages)
  const ownerIconMapping = {
    Shared: <Diversity3Icon />,
    My: <SentimentVerySatisfiedIcon />,
    Tatoeba: <StorageIcon />
  }
  const labelIconMapping = {
    Sentences: <SmsIcon />,
    Many: <SmsIcon />
  }
  const queries = {
    Sentences: {
      My: {
        query: USER_SENTENCES_WITH_TRANSLATIONS_QUERY,
        mapper: mapUserSentencesDataSpecificLang,
        variables: {
          username: username,
          lang: getLanguageAcronym(sourceLanguage),
          translationLang: getLanguageAcronym(targetLanguage)
        }
      },
      Shared: {
        query: SHARED_SENTENCES_WITH_TRANSLATIONS_QUERY,
        mapper: mapSharedSentencesDataSpecificLang,
        variables: {
          username: username,
          lang: getLanguageAcronym(sourceLanguage),
          translationLang: getLanguageAcronym(targetLanguage)
        }
      },
      Tatoeba: {
        query: TATOEBA_SENTENCES_QUERY,
        mapper: mapTatoebaSentencesData,
        variables: {
          lang: getLanguageAcronym(sourceLanguage),
          word: searchWord,
          translationLang: getLanguageAcronym(targetLanguage)
        }
      }
    },
    Many: {
      My: {
        query: GET_ALL_TRANSLATION_USER_SENTENCES,
        mapper: mapUserSentencesDataOneToMany,
        variables: {
          username: username,
          sourceLang: getLanguageAcronym(sourceLanguage)
        }
      },
      Shared: {
        query: GET_ALL_TRANSLATION_SHARED_SENTENCES,
        mapper: mapSharedSentencesDataOneToMany,
        variables: {
          username: username,
          sourceLang: getLanguageAcronym(sourceLanguage)
        }
      },
      Tatoeba: {
        query: TATOEBA_SENTENCES_QUERY,
        mapper: mapTatoebaSentencesDataOneToMany,
        variables: {
          lang: getLanguageAcronym(sourceLanguage),
          word: searchWord,
          translationLang: null
        }
      }
    }
  }
  const handleSelectLabel = (selectedLabel) => {
    setLabel(selectedLabel)
  }
  // --- FIX: Use 3-letter acronym for setting spokenLang ---
  const handleLanguageChange = (selectedLanguage) => {
    setTargetLanguage(selectedLanguage)
    // CRITICAL CHANGE: Use getLanguageAcronym to match GraphQL/languages.js
    setSpokenLang(getLanguageAcronym(selectedLanguage))
  }
  // --- END FIX ---
  const handleSearch = async () => {
    if (!queries[label] || !queries[label][owner] || !username) return
    setLoadingFetch(true)
    const selectedQuery = queries[label][owner].query
    const variables = queries[label][owner].variables
    try {
      const { data } = await apolloClient.query({
        query: selectedQuery,
        variables,
        fetchPolicy: 'network-only'
      })
      const transformedData = queries[label][owner].mapper(data)
      setSentenceListInternal(transformedData)
      setCurrentTranslationIndex(0)
    } catch (error) {
      console.error('Fetch error:', error)
    }
    setLoadingFetch(false)
  }
  useEffect(() => {
    if (username) {
      handleSearch()
    }
  }, [label, owner, username, ...(label !== 'Many' ? [targetLanguage] : [])])
  useEffect(() => {
    if (sentenceListInternal.length > 0) {
      const current = sentenceListInternal[currentTranslationIndex]
      let expected = ''
      if (label === 'Many') {
        const translation = current.translations.find((t) => t.targetLanguage === spokenLang)
        expected = translation ? translation.target : 'No translation available for this language'
      } else {
        expected = current?.target || ''
      }
      setExpectedText(expected)
      setHighlightedExpected([])
      setCorrectWords(new Set()) // Reset correct words when changing sentence
    } else {
      setExpectedText('')
      setHighlightedExpected([])
      setCorrectWords(new Set())
    }
  }, [sentenceListInternal, currentTranslationIndex, label, spokenLang])
  useGSAP(() => {
    if (targetRef.current) {
      const splitTarget = new SplitText(targetRef.current, { type: 'words' })
      gsap.from(splitTarget.words, { opacity: 0, y: 50, stagger: 0.1, duration: 2, ease: 'power1.out' })
      // Cleanup
      return () => {
        splitTarget.revert()
      }
    }
  }, [currentTranslationIndex, spokenText, expectedText])
  const handleClickTranslation = useCallback(() => {
    const voiceLangCode = getLanguageAcronym(targetLanguage)
    const voice = voices[voiceLangCode]
    if (!voice) {
      console.warn(`No voice for ${targetLanguage} (${voiceLangCode}), using English fallback`)
      speak(expectedText, voices['eng'], voiceSpeed)
    } else {
      speak(expectedText, voice, voiceSpeed)
    }
  }, [expectedText, targetLanguage, voiceSpeed])
  const handleTouchStart = (e) => {
    swipeStartX.current = e.touches[0].clientX
    swipeStartY.current = e.touches[0].clientY
  }
  const handleTouchMove = (e) => {
    swipeEndX.current = e.touches[0].clientX
    swipeEndY.current = e.touches[0].clientY
  }
  const handleTouchEnd = () => {
    const swipeDistanceX = swipeEndX.current - swipeStartX.current
    const swipeDistanceY = swipeEndY.current - swipeStartY.current
    if (swipeDistanceX > 50) {
      setCurrentTranslationIndex((prevIndex) => (prevIndex - 1 < 0 ? sentenceListInternal.length - 1 : prevIndex - 1))
    } else if (swipeDistanceX < -50) {
      setCurrentTranslationIndex((prevIndex) => (prevIndex + 1 === sentenceListInternal.length ? 0 : prevIndex + 1))
    }
    if (swipeDistanceY < -150) {
      setIsAllViewSpeechPracticeVisible(false)
    }
  }
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isMobile && isAllViewSpeechPracticeVisible) {
        if (event.key === 'ArrowRight') {
          setCurrentTranslationIndex((prevIndex) => (prevIndex + 1 === sentenceListInternal.length ? 0 : prevIndex + 1))
        } else if (event.key === 'ArrowLeft') {
          setCurrentTranslationIndex((prevIndex) => (prevIndex - 1 < 0 ? sentenceListInternal.length - 1 : prevIndex - 1))
        } else if (event.key === 'e') {
          setIsAllViewSpeechPracticeVisible(false)
        } else if (event.key === 'r' || event.key === 'R') {
          const randomIndex = Math.floor(Math.random() * sentenceListInternal.length)
          setCurrentTranslationIndex(randomIndex)
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [sentenceListInternal.length, isMobile, isAllViewSpeechPracticeVisible])
  const normalizeText = (text) => {
    return text
      ? text
          .toLowerCase()
          .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()?\¿\¡"]/g, '') // Added ? ¿ ¡ " for full punctuation insensitivity
          .replace(/\s{2,}/g, ' ')
          .trim()
      : ''
  }
  const handleSpeechResult = (text) => {
    setSpokenText(text)
    const expectedWords = expectedText.trim().split(/\s+/)
    const spokenWords = text.trim().split(/\s+/)
    const normExpectedWords = expectedWords.map(normalizeText)
    const normSpokenWords = spokenWords.map(normalizeText)
    const newCorrect = new Set(correctWords)
    const remainingIndices = Array.from({ length: expectedWords.length }, (_, i) => i).filter((i) => !correctWords.has(i))
    for (let j = 0; j < Math.min(remainingIndices.length, normSpokenWords.length); j++) {
      if (normExpectedWords[remainingIndices[j]] === normSpokenWords[j]) {
        newCorrect.add(remainingIndices[j])
      }
    }
    setCorrectWords(newCorrect)
    const highlighted = expectedWords.map((word, i) => ({
      text: word,
      color: newCorrect.has(i) ? 'green' : 'red'
    }))
    setHighlightedExpected(highlighted)
    if (newCorrect.size === expectedWords.length) {
      setFeedback('Correct!')
      setTimeout(() => {
        setCurrentTranslationIndex((prev) => (prev + 1) % sentenceListInternal.length)
        setSpokenText('')
        setFeedback('')
        setHighlightedExpected([])
        setCorrectWords(new Set())
      }, 2000)
    } else if (text) {
      setFeedback('Try again')
    }
  }


  return (
    <div
      className={styles.translationFullView}
      onTouchStart={isMobile ? handleTouchStart : null}
      onTouchMove={isMobile ? handleTouchMove : null}
      onTouchEnd={isMobile ? handleTouchEnd : null}>
      {/* Controls Div */}
      <div className={styles.controls}>
        <div style={{ display: 'flex', gap: '10px' }}>
          {/* ... SpeedDials ... */}
          <SpeedDial
            onOpen={() => setIsOwnerSpeedDialOpen(true)}
            onClose={() => setIsOwnerSpeedDialOpen(false)}
            ariaLabel="Owner SpeedDial"
            FabProps={{ variant: 'extended' }}
            icon={
              <Box>
                <Typography>{owner}</Typography>
              </Box>
            }
            direction="down">
            {owners.map((ownerItem) => {
              const icon = ownerIconMapping[ownerItem] || <WorkOutlineIcon />
              return (
                <SpeedDialAction
                  key={ownerItem}
                  icon={icon}
                  tooltipTitle={ownerItem}
                  onClick={() => setOwner(ownerItem)}
                  style={{ height: isAnySpeedDialOpen() ? 'auto' : '2px' }}
                />
              )
            })}
          </SpeedDial>
          <SpeedDial
            onOpen={() => setIsLabelSpeedDialOpen(true)}
            onClose={() => setIsLabelSpeedDialOpen(false)}
            ariaLabel="Label SpeedDial"
            FabProps={{ variant: 'extended' }}
            icon={
              <Box>
                <Typography sx={{ fontSize: '18px' }}>{label}</Typography>
              </Box>
            }
            direction="down">
            {labels.map((labelItem) => {
              const icon = labelIconMapping[labelItem] || <WorkOutlineIcon />
              return (
                <SpeedDialAction
                  key={labelItem}
                  icon={icon}
                  tooltipTitle={labelItem}
                  onClick={() => handleSelectLabel(labelItem)}
                  style={{ height: isAnySpeedDialOpen() ? 'auto' : '2px' }}
                />
              )
            })}
          </SpeedDial>
          <SpeedDial
            onOpen={() => setIsLanguageSpeedDialOpen(true)}
            onClose={() => setIsLanguageSpeedDialOpen(false)}
            ariaLabel="Language SpeedDial"
            FabProps={{ variant: 'extended' }}
            icon={
              <Box>
                {/* component="div": Typography defaults to <p>, and a <div> inside a <p> is invalid HTML */}
                <Typography noWrap={true} component="div" sx={{ fontSize: '18px' }}>
                  {flags[getLanguageAcronym(targetLanguage)]} <div className={styles.languageTitle}>{targetLanguage}</div>
                </Typography>
              </Box>
            }
            direction="down">
            {languageOptions.map((languageCode) => {
              const languageName = languages[languageCode]
              return (
                <SpeedDialAction
                  key={languageCode}
                  icon={
                    <Box>
                      <Typography>{flags[languageCode]}</Typography>
                    </Box>
                  }
                  tooltipTitle={languageName}
                  tooltipOpen
                  onClick={() => handleLanguageChange(languageName)}
                  style={{ height: isAnySpeedDialOpen() ? 'auto' : '2px' }}
                />
              )
            })}
          </SpeedDial>
        </div>
      </div>
      {loadingFetch && <CircularProgress className={styles.loading} />}
      {/* --- DISPLAY UPDATED HERE --- */}
      <div className={styles.spoken}>
        {/* Source Language (The Prompt) */}
        <span className={styles.textNormal} style={{ opacity: 0.7 }}>
          {sentenceListInternal[currentTranslationIndex]?.source || ''}
        </span>
      </div>
      <div className={styles.translation}>
        {/* Target Language (What to read/speak) */}
        <span
          ref={targetRef}
          className={styles.textNormal}
          onClick={isMobile ? undefined : handleClickTranslation}
          onPointerUp={isMobile ? handleClickTranslation : undefined}>
          {highlightedExpected.length > 0
            ? highlightedExpected.map((w, i) => (
                <span key={i} style={{ color: w.color }}>
                  {w.text}
                  {i < highlightedExpected.length - 1 ? ' ' : ''}
                </span>
              ))
            : expectedText}
        </span>
      </div>
      {/* ---------------------------- */}
      <div className={styles.button}>
        <p>Practice Language</p>
        <SpeechPracticeRecorder spokenLang={spokenLang} onSpeechResult={handleSpeechResult} />
        {owner === 'Tatoeba' && (
          <TextField
            margin="dense"
            label="Search Word"
            type="text"
            fullWidth
            value={searchWord}
            onChange={(e) => setSearchWord(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') handleSearch()
            }}
          />
        )}
        <p>Your Speech: {spokenText}</p>
        <p>Feedback: {feedback}</p>
        <Fab color="primary" onClick={handleSearch}>
          <RefreshIcon />
        </Fab>
      </div>
    </div>
  )
}
